import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, 
  Users, 
  Award, 
  Star, 
  Download,
  Search,
  BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const ProviderComparison = () => {
  const [selectedProviders, setSelectedProviders] = useState(['1', '2', '3']);
  const [searchTerm, setSearchTerm] = useState('');

  const providers = [
    { id: '1', name: 'Dr. Gerard Nikodem', referrals: 237, score: 92, satisfaction: 98, responseTime: 2.3 },
    { id: '2', name: 'Dr. Monya Phung', referrals: 174, score: 88, satisfaction: 96, responseTime: 3.1 },
    { id: '3', name: 'Dr. David Schertzer', referrals: 158, score: 85, satisfaction: 94, responseTime: 4.2 },
    { id: '4', name: 'Dr. Justin Garner', referrals: 114, score: 82, satisfaction: 93, responseTime: 3.8 },
    { id: '5', name: 'Dr. Elizabeth Fassler', referrals: 100, score: 80, satisfaction: 95, responseTime: 2.9 }
  ];

  const getComparisonData = () => {
    return providers
      .filter(p => selectedProviders.includes(p.id))
      .map(p => ({
        name: p.name.replace('Dr. ', ''),
        'Total Referrals': p.referrals,
        'Relationship Score': p.score,
        'Satisfaction %': p.satisfaction,
        'Response Time (hrs)': p.responseTime
      }));
  };

  const getRadarData = () => {
    const selected = providers.filter(p => selectedProviders.includes(p.id));
    const metrics = ['Referrals', 'Score', 'Satisfaction', 'Speed'];
    
    return metrics.map(metric => {
      const obj = { metric };
      selected.forEach(provider => {
        const normalized = metric === 'Referrals' ? (provider.referrals / 250) * 100 :
                          metric === 'Score' ? provider.score :
                          metric === 'Satisfaction' ? provider.satisfaction :
                          (5 - provider.responseTime) * 20; // Invert response time
        obj[provider.name.replace('Dr. ', '')] = Math.round(normalized);
      });
      return obj;
    });
  };

  const exportComparison = (format) => {
    const data = getComparisonData();
    
    if (format === 'csv') {
      let csvContent = 'Provider,Total Referrals,Relationship Score,Satisfaction %,Response Time (hrs)\n';
      data.forEach(row => {
        csvContent += `${row.name},${row['Total Referrals']},${row['Relationship Score']},${row['Satisfaction %']},${row['Response Time (hrs)']}\n`;
      });
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'provider_comparison.csv';
      link.click();
    } else if (format === 'pdf') {
      alert('PDF export would generate detailed comparison report with charts');
    }
  };

  const toggleProvider = (providerId) => {
    if (selectedProviders.includes(providerId)) {
      if (selectedProviders.length > 1) {
        setSelectedProviders(selectedProviders.filter(id => id !== providerId));
      } else {
        alert('At least one provider must be selected');
      }
    } else {
      if (selectedProviders.length < 5) {
        setSelectedProviders([...selectedProviders, providerId]);
      } else {
        alert('Maximum 5 providers can be compared at once');
      }
    }
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
            <BarChart3 className="h-8 w-8 mr-3 text-[#D4AF37]" />
            Provider Comparison
          </h1>
          <p className="text-gray-600">Compare performance metrics across providers</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => exportComparison('csv')}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => exportComparison('pdf')}>
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Provider Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Select Providers to Compare</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {providers.map(provider => (
              <Button
                key={provider.id}
                variant={selectedProviders.includes(provider.id) ? 'default' : 'outline'}
                onClick={() => toggleProvider(provider.id)}
                className={selectedProviders.includes(provider.id) ? 'bg-[#D4AF37] text-[#0A1628]' : ''}
              >
                {provider.name}
              </Button>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-3">
            {selectedProviders.length} of 5 providers selected
          </p>
        </CardContent>
      </Card>

      {/* Comparison Charts */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Bar Chart Comparison */}
        <Card>
          <CardHeader>
            <CardTitle>Referral Volume Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getComparisonData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Total Referrals" fill="#D4AF37" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Radar Chart - Overall Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Performance Radar</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={getRadarData()}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" />
                <PolarRadiusAxis domain={[0, 100]} />
                {providers
                  .filter(p => selectedProviders.includes(p.id))
                  .map((provider, index) => {
                    const colors = ['#D4AF37', '#0A1628', '#22c55e', '#3b82f6', '#a855f7'];
                    return (
                      <Radar
                        key={provider.id}
                        name={provider.name.replace('Dr. ', '')}
                        dataKey={provider.name.replace('Dr. ', '')}
                        stroke={colors[index]}
                        fill={colors[index]}
                        fillOpacity={0.3}
                      />
                    );
                  })
                }
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-semibold text-[#0A1628]">Provider</th>
                  <th className="text-center p-3 font-semibold text-[#0A1628]">Total Referrals</th>
                  <th className="text-center p-3 font-semibold text-[#0A1628]">Relationship Score</th>
                  <th className="text-center p-3 font-semibold text-[#0A1628]">Satisfaction Rate</th>
                  <th className="text-center p-3 font-semibold text-[#0A1628]">Avg Response Time</th>
                  <th className="text-center p-3 font-semibold text-[#0A1628]">Rank</th>
                </tr>
              </thead>
              <tbody>
                {providers
                  .filter(p => selectedProviders.includes(p.id))
                  .sort((a, b) => b.score - a.score)
                  .map((provider, index) => (
                    <tr key={provider.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium text-[#0A1628]">{provider.name}</td>
                      <td className="p-3 text-center">{provider.referrals}</td>
                      <td className="p-3 text-center">
                        <Badge className="bg-[#D4AF37] text-[#0A1628]">{provider.score}</Badge>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-green-600 font-semibold">{provider.satisfaction}%</span>
                      </td>
                      <td className="p-3 text-center">{provider.responseTime} hrs</td>
                      <td className="p-3 text-center">
                        <Badge variant="outline">#{index + 1}</Badge>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProviderComparison;